import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import {
  MessageCircle, Zap, TrendingUp, FileText, AlertCircle,
  LogOut, Calculator, Landmark, FlaskConical, MapPin,
  Users, Tractor, Globe, BarChart3,
} from 'lucide-react';

const MODES_BR = [
  { id: 'bndes-automatico', title: 'BNDES Automático', description: 'Máquinas, equipamentos e capital de giro', icon: Zap, color: '#2563eb' },
  { id: 'finame', title: 'FINAME', description: 'Financiamento exclusivo de equipamentos', icon: Landmark, color: '#0891b2' },
  { id: 'capital-giro', title: 'Capital de Giro', description: 'Recursos para operação e fluxo de caixa', icon: TrendingUp, color: '#059669' },
  { id: 'inovacao-finep', title: 'Inovação & Finep', description: 'P&D, subvenção e FAPs estaduais', icon: FlaskConical, color: '#7c3aed' },
  { id: 'fomento-regional', title: 'Fomento Regional', description: 'Desenvolve SP, BDMG, BRDE, BNB, BASA', icon: MapPin, color: '#0d9488' },
  { id: 'renovacao', title: 'Renovação de Máquinas', description: 'Planejamento de substituição', icon: AlertCircle, color: '#16a34a' },
  { id: 'incentivos', title: 'Incentivos Fiscais', description: 'ICMS, IPI, Lei do Bem', icon: FileText, color: '#9333ea' },
  { id: 'mei-autonomo', title: 'MEI & Autônomo', description: 'Microcrédito e linhas para PF', icon: Users, color: '#ea580c' },
  { id: 'rural-agro', title: 'Rural & Agro', description: 'PRONAF, PRONAMP, crédito rural', icon: Tractor, color: '#65a30d' },
  { id: 'documentacao', title: 'Documentação', description: 'Checklist completo de documentos', icon: FileText, color: '#d97706' },
  { id: 'historico', title: 'Histórico & Alertas', description: 'Rastreamento de contratos', icon: MessageCircle, color: '#db2777', premium: true },
];

const MODES_EUA = [
  { id: 'eua-financing', title: '🇺🇸 SBA Loans', description: 'SBA 7(a), SBA 504 e SBA Express', icon: Globe, color: '#1d4ed8', tag: 'SBA 7(a) • SBA 504' },
  { id: 'eua-financing', title: '🇺🇸 SBIR / STTR', description: 'Grants federais para inovação', icon: FlaskConical, color: '#7c3aed', tag: 'NSF • NIH • DoD' },
  { id: 'eua-financing', title: '🇺🇸 USDA / FSA', description: 'Crédito rural americano', icon: Tractor, color: '#16a34a', tag: 'Farm Service Agency' },
  { id: 'eua-financing', title: '🇺🇸 CDFIs', description: 'Bancos comunitários e crédito para minorias', icon: MapPin, color: '#d97706', tag: 'Community Development' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'brasil' | 'eua'>('brasil');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const fetchStats = async () => {
      try {
        const data = await api.getStats();
        setStats(data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, [user, navigate]);

  const handleLogout = () => { logout(); navigate('/login'); };

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, border: '4px solid #e0e0e0', borderTopColor: '#059669', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
          <p style={{ marginTop: 16, color: '#6b7280', fontSize: 15 }}>Carregando...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* Header */}
      <header style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#059669', margin: 0, letterSpacing: '-0.5px' }}>Inteligência Financeira</h1>
            <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Bem-vindo, {user?.fullName || user?.email}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>Plano</p>
              <p style={{ fontSize: 14, fontWeight: 700, color: user?.subscriptionTier === 'premium' ? '#059669' : '#374151', margin: 0 }}>
                {user?.subscriptionTier === 'premium' ? '⭐ Premium' : 'Gratuito'}
              </p>
            </div>
            <button onClick={handleLogout} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 14px', fontSize: 13, color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>

        {/* Stats */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            {[
              { label: 'Perguntas Hoje', value: `${stats.questionsToday}/${stats.questionsLimit}`, sub: 'Renova à meia-noite', color: '#2563eb' },
              { label: 'Conversas', value: stats.totalConversations, color: '#059669' },
              { label: 'Simulações', value: stats.totalSimulations, color: '#7c3aed' },
              { label: 'Mercados', value: '🇧🇷 🇺🇸', color: '#d97706' },
            ].map((s, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 8px' }}>{s.label}</p>
                <p style={{ fontSize: 32, fontWeight: 800, color: s.color, margin: 0, letterSpacing: '-1px' }}>{s.value}</p>
                {s.sub && <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>{s.sub}</p>}
              </div>
            ))}
          </div>
        )}

        {/* CTA Premium */}
        {user?.subscriptionTier === 'free' && (
          <div style={{ background: 'linear-gradient(135deg, #eff6ff, #ecfeff)', border: '1px solid #bfdbfe', borderRadius: 16, padding: '20px 28px', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: '#1e3a8a', margin: '0 0 4px' }}>Desbloqueie o Premium</h3>
              <p style={{ fontSize: 14, color: '#3b82f6', margin: 0 }}>Perguntas ilimitadas, alertas e relatórios em PDF — R$ 99/mês</p>
            </div>
            <button onClick={() => navigate('/pricing')} style={{ background: 'linear-gradient(135deg, #2563eb, #0891b2)', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Assinar Agora
            </button>
          </div>
        )}

        {/* Ações Rápidas */}
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Ações Rápidas</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {[
              { icon: Calculator, label: 'Simulador', desc: 'Calcular financiamento', color: '#2563eb', bg: '#eff6ff', path: '/simulator' },
              { icon: FileText, label: 'Documentação', desc: 'Checklist completo', color: '#ea580c', bg: '#fff7ed', path: '/checklist' },
              { icon: BarChart3, label: 'Relatórios', desc: 'Meus documentos', color: '#7c3aed', bg: '#f5f3ff', path: '/reports' },
            ].map((a, i) => {
              const Icon = a.icon;
              return (
                <div key={i} onClick={() => navigate(a.path)}
                  style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '18px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = a.color; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.transform = 'none'; }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: a.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={22} color={a.color} />
                  </div>
                  <div>
                    <p style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>{a.label}</p>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>{a.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Modos */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>Escolha um Modo</h2>
            <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 12, padding: 4, gap: 4 }}>
              <button onClick={() => setActiveTab('brasil')}
                style={{ padding: '8px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, background: activeTab === 'brasil' ? '#fff' : 'transparent', color: activeTab === 'brasil' ? '#059669' : '#6b7280', boxShadow: activeTab === 'brasil' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                🇧🇷 Brasil
              </button>
              <button onClick={() => setActiveTab('eua')}
                style={{ padding: '8px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, background: activeTab === 'eua' ? '#fff' : 'transparent', color: activeTab === 'eua' ? '#059669' : '#6b7280', boxShadow: activeTab === 'eua' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                🇺🇸 EUA
              </button>
            </div>
          </div>

          {/* Grid Brasil */}
          {activeTab === 'brasil' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {MODES_BR.map((mode, i) => {
                const Icon = mode.icon;
                const isPremium = mode.premium && user?.subscriptionTier === 'free';
                return (
                  <div key={i}
                    onClick={() => !isPremium && navigate(`/chat/${mode.id}`)}
                    style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: '22px 24px', cursor: isPremium ? 'not-allowed' : 'pointer', opacity: isPremium ? 0.5 : 1, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'all 0.2s' }}
                    onMouseEnter={e => { if (!isPremium) { e.currentTarget.style.borderColor = mode.color; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 4px 12px rgba(0,0,0,0.1)`; }}}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
                  >
                    <div style={{ width: 48, height: 48, borderRadius: 14, background: `${mode.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                      <Icon size={24} color={mode.color} />
                    </div>
                    <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>{mode.title}</h3>
                    <p style={{ fontSize: 14, color: '#6b7280', margin: 0, lineHeight: 1.5 }}>{mode.description}</p>
                    {isPremium && <p style={{ fontSize: 12, color: '#2563eb', fontWeight: 600, margin: '10px 0 0' }}>🔒 Apenas Premium</p>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Grid EUA */}
          {activeTab === 'eua' && (
            <div>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 14, padding: '14px 18px', marginBottom: 20 }}>
                <p style={{ fontSize: 14, color: '#1e40af', margin: 0 }}>
                  <strong>🇺🇸 Mercado Americano</strong> — Orientações para brasileiros com empresas nos EUA ou que desejam acessar financiamento público americano (SBA, SBIR, USDA, CDFIs).
                </p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                {MODES_EUA.map((mode, i) => {
                  const Icon = mode.icon;
                  return (
                    <div key={i}
                      onClick={() => navigate(`/chat/${mode.id}`)}
                      style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: '22px 24px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'all 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = mode.color; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 4px 12px rgba(0,0,0,0.1)`; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
                    >
                      <div style={{ width: 48, height: 48, borderRadius: 14, background: `${mode.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                        <Icon size={24} color={mode.color} />
                      </div>
                      <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>{mode.title}</h3>
                      <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 12px', lineHeight: 1.5 }}>{mode.description}</p>
                      <span style={{ display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999 }}>{mode.tag}</span>
                    </div>
                  );
                })}
              </div>

              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 16, padding: '24px', marginTop: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>💡 Como funciona o crédito público nos EUA</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                  {[
                    { title: 'SBA não empresta diretamente', desc: 'O governo garante até 85% do empréstimo feito por bancos parceiros, reduzindo o risco para que eles emprestem para PMEs.' },
                    { title: 'SBIR são grants', desc: 'Dinheiro que não precisa ser devolvido. Focado em inovação tecnológica. Altamente competitivo mas muito valioso.' },
                    { title: 'LLC ou C-Corp é obrigatório', desc: 'Para acessar qualquer programa, a empresa precisa ter registro legal nos EUA com operação real.' },
                  ].map((c, i) => (
                    <div key={i}>
                      <p style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>{c.title}</p>
                      <p style={{ fontSize: 13, color: '#6b7280', margin: 0, lineHeight: 1.6 }}>{c.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}